import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { DragDropProvider } from '@dnd-kit/react';
import { AutoScroller } from '@dnd-kit/dom';
import { move } from '@dnd-kit/helpers';
import { useResizeDetector } from 'react-resize-detector';
import { useDispatch } from 'react-redux';
import { selectOverview, selectTenantId } from '../../../../util/store/selectors';
import { getStore, useSelector } from "../../../../util/store/store";
import { CONTRACT_TYPE } from "../../../../util/varibles/constants";
import { checkLimit } from "../../../../util/varibles/global";
import AvailableTimes from "../AvailableTimes";
import Contracts from "../Contracts";
import Operations from "../Operations";
import Weeks from "../Weeks";
import Month from "./Month";
import styles from './style.module.scss';
import Vessel from "./Vessel";
import { overviewActions } from '../../reducer';
import useDragScrollUp from '../../useDragScrollUp';
import Menu from '../../../../components/Menu';
import { loginActions } from '../../../LoginPage/reducer';
import { OverviewSetting } from '../../../../util/varibles/userSetting';

export const HEIGHT_PER_OP = 38;
export const OPERATION_HEIGHT = 20;
export const OPERATION_EDGE_GAP = 33;
export const OPERATION_VERTICAL_OFFSET = -10;
const verticalAutoScroll = (plugins: any[]) => plugins.map(plugin =>
    plugin === AutoScroller
        ? AutoScroller.configure({ threshold: { x: 0, y: .2 } })
        : plugin
);
export interface IVesselHeight {
    [vesselId: string]: { height: number, index: number, position: number }
}

interface IProps {
    calendarRef: React.RefObject<HTMLDivElement>
}

const Vessels: React.FC<IProps> = ({ calendarRef }) => {
    const dispatch = useDispatch();
    const vessels = useSelector(selectOverview.vessels);
    const [menuOpen, setMenuOpen] = useState(false);
    const getScrollElement = useCallback(() => calendarRef.current, [calendarRef]);
    const { onDragMove, stopDragScrollUp } = useDragScrollUp(getScrollElement);

    const { list, contracts, available_times, totalHeight, vesselHeight } = useMemo(() => {
        const userTenantId = selectTenantId(getStore());

        return vessels.reduce((rs: any, item) => {
            const { id, contracts, available_times, isOwn, permission, isShow, row = 1 } = item;
            const isPermission = !!(isOwn || permission);
            if (isShow) {
                const vesselIndex = rs.list.length;
                const operationsHeight = row * HEIGHT_PER_OP + OPERATION_HEIGHT;
                const height = operationsHeight + OPERATION_EDGE_GAP * 2;

                rs.vesselHeight[id] = {
                    index: rs.index,
                    height,
                    position: vesselIndex,
                };

                rs.totalHeight += height;

                rs.index += height;

                rs.list.push(item);

                available_times.forEach((sub: any) => {
                    rs.available_times.push({ ...sub, vesselId: id, isPermission, vesselIndex });
                })

                contracts.forEach((sub: any) => {
                    const { type, tenant_id } = sub;
                    if (type === CONTRACT_TYPE.FIXED) {
                        rs.contracts.push({
                            ...sub,
                            isOwn: tenant_id === userTenantId,
                            vesselId: id,
                            isPermission,
                            vesselIndex
                        });
                    }
                })
            }
            return rs;
        }, { list: [], contracts: [], available_times: [], index: 0, totalHeight: 0, vesselHeight: {} });
    }, [vessels])

    const count = list.length;

    const handleSortByName = useCallback(() => {
        const nextVessels = [...vessels].sort((first, second) =>
            first.name.localeCompare(second.name, undefined, { sensitivity: 'base', numeric: true })
        );
        dispatch(overviewActions.updateVessels(nextVessels));
        dispatch(loginActions.updateUserSetting([{
            [OverviewSetting.VESSEL_ORDER]: [],
        }]));
    }, [dispatch, vessels]);

    const handleDragEnd = useCallback(() => {
        stopDragScrollUp();
        const currentVessels = selectOverview.vessels(getStore());
        dispatch(loginActions.updateUserSetting([{
            [OverviewSetting.VESSEL_ORDER]: currentVessels
                .filter(vessel => vessel.isShow)
                .map(vessel => vessel.id),
        }]));
    }, [dispatch, stopDragScrollUp]);

    const handleDragOver = useCallback((event: any) => {
        const nextVisibleVessels = move(list, event);
        if (nextVisibleVessels === list)
            return;

        const hiddenVessels = vessels.filter(vessel => !vessel.isShow);
        const nextVessels = [...nextVisibleVessels, ...hiddenVessels];
        dispatch(overviewActions.updateVessels(nextVessels));
    }, [dispatch, list, vessels]);

    const updateSizeVariables = () => {
        const calendar = calendarRef.current;
        if (!calendar) return;

        const piece = checkLimit(0, undefined, calendar.clientHeight - totalHeight - 60);
        const moreHeight = count ? piece / count : 0;
        calendar.style.setProperty('--more-height', `${moreHeight}px`);

        Object.keys(vesselHeight).forEach(id => {
            const { height, index, position } = vesselHeight[id];
            calendar.style.setProperty(`--${id}-height`, `${height}px`);
            calendar.style.setProperty(`--${id}-top`, `${index + 60 + position * moreHeight}px`);
            const contentTop = OPERATION_EDGE_GAP + moreHeight / 2 + OPERATION_VERTICAL_OFFSET;
            calendar.style.setProperty(`--${id}-content-top`, `${contentTop}px`);
            calendar.style.setProperty(`--${id}-operation-top`, `${index + 60 + position * moreHeight + contentTop}px`);
        });
    };

    useLayoutEffect(updateSizeVariables, [calendarRef, count, totalHeight, vesselHeight]);
    useResizeDetector({ targetRef: calendarRef, onResize: updateSizeVariables });

    return <>
        <Menu
            className={styles.vesselsMenu}
            trigger={['contextMenu']}
            visible={menuOpen}
            onOpenChange={setMenuOpen}
            items={[{
                key: 'sort-by-name',
                label: 'Sort by name',
                onClick: handleSortByName,
            }]}
        >
            <div
                className={styles.vessels}
            >
                <Month />
                <DragDropProvider
                    plugins={verticalAutoScroll}
                    onDragStart={() => setMenuOpen(false)}
                    onDragMove={onDragMove}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                >
                    {list.map((item: any, index: number) => <Vessel
                        key={item.id}
                        index={index}
                        data={item}
                        vesselHeight={vesselHeight}
                    />)}
                </DragDropProvider>
            </div>
        </Menu>
        <AvailableTimes vesselHeight={vesselHeight} source={available_times} />
        <Contracts vesselHeight={vesselHeight} source={contracts} />
        <Operations vesselHeight={vesselHeight} />
        <Weeks vessels={list} vesselHeight={vesselHeight} />
    </>;
};

export default Vessels;
